"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

/**
 * useConfirm — promise-returning replacement for window.confirm.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete this?", danger: true }))) return;
 *
 * Renders an in-app ConfirmDialog instead of the native browser prompt:
 * styled to match the design system, dismissable via Escape / backdrop /
 * Cancel, and consistent across desktop and mobile (native confirm() looks
 * dreadful on iOS Safari and breaks the design language).
 */

export type ConfirmOptions = {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

type PendingConfirm = ConfirmOptions & {
  resolve: (value: boolean) => void;
};

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...opts, resolve });
    });
  }, []);

  const settle = useCallback((value: boolean) => {
    setPending((cur) => {
      if (cur) cur.resolve(value);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") settle(false);
      else if (e.key === "Enter") settle(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending, settle]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <ConfirmDialog
          title={pending.title}
          body={pending.body}
          confirmLabel={pending.confirmLabel}
          cancelLabel={pending.cancelLabel}
          danger={pending.danger}
          onConfirm={() => settle(true)}
          onCancel={() => settle(false)}
        />
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    if (typeof console !== "undefined") {
      console.warn(
        "[confirm] useConfirm() used outside <ConfirmProvider>; falling back to window.confirm",
      );
    }
    return async (opts) => {
      if (typeof window === "undefined") return false;
      const msg = opts.body ? `${opts.title}\n\n${opts.body}` : opts.title;
      return window.confirm(msg);
    };
  }
  return ctx;
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel,
  danger,
  onConfirm,
  onCancel,
}: {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} aria-hidden />
      <div className="relative w-full max-w-md bg-card border border-border rounded-lg shadow-xl p-5">
        <h3 id="confirm-dialog-title" className="text-sm font-semibold mb-1">
          {title}
        </h3>
        {body && <p className="text-sm text-subtle mb-4 whitespace-pre-line">{body}</p>}
        <div className="flex justify-end gap-2 mt-2">
          <button
            onClick={onCancel}
            className="text-xs px-3 py-1.5 rounded border border-border hover:bg-muted"
          >
            {cancelLabel ?? "Cancel"}
          </button>
          <button
            autoFocus
            onClick={onConfirm}
            className={
              danger
                ? "text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700"
                : "text-xs px-3 py-1.5 rounded bg-accent text-accent-ink hover:bg-accent/90"
            }
          >
            {confirmLabel ?? "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
